<!-- The pager. A photoreal render of the body (public/devices, decorative)
     with the live <Lcd> and REAL <button> keys laid exactly over it, from the
     rectangles in src/pm/device-art.js — never one picture with tap zones:
     every key is a button with a name, a focus ring and a pressed state, and
     its silkscreen legend is real text printed on the body.

     numeric / alpha — the inbox, one page at a time (▲▼ READ REPLY NEW LOCK DEL)
     twoway          — the conversation list (▲▼ OPEN, keyboard = COMPOSE)
     MODE (Devices) and SETUP (Settings) on every model.

     The box is a fixed aspect ratio, so the glass and the keys never move —
     whatever the page, state or text size. `thumb` renders it static: no
     timers, no buttons, hidden from assistive tech.

     A key's hit area is its drawn cap plus its legend, and never smaller than
     --tap. Every key has an accessible name that contains its legend. -->
<template>
  <div
    class="pm-dev"
    :class="[
      `pm-dev--${device.id}`,
      `pm-dev--${device.family}`,
      `pm-dev--silk-${art.silk}`,
      {
        'pm-dev--thumb': thumb,
        'pm-dev--large': large,
        'is-offline': !online,
      },
    ]"
    :style="devStyle"
  >
    <!-- Large controls: only the top of the device — the glass — shows; the
         keys below it are the big buttons instead of dead art. -->
    <div class="pm-dev__crop">
      <div class="pm-dev__art" :class="{ 'is-shaking': shaking }">
        <img class="pm-dev__img" :src="art.src" alt="" draggable="false" decoding="async">

        <span
          v-if="art.led"
          class="pm-dev__led"
          :class="{ 'is-on': unreadInbox > 0 }"
          :style="ledStyle"
          aria-hidden="true"
        />

        <span
          class="pm-dev__name"
          :class="[`is-${art.name.tone || art.silk}`, { 'is-left': art.name.align === 'left' }]"
          :style="{ left: `${art.name.x}%`, top: `${art.name.y}%` }"
          aria-hidden="true"
        >{{ device.name }}<span class="pm-dev__year">{{ device.year }}</span></span>

        <div class="pm-dev__screen" :style="screenStyle">
          <Lcd
            ref="lcd"
            :kind="device.lcd"
            :font="device.font"
            :glow-key="thumb ? null : arrivalKey"
            :style="{ '--lines': device.lines, '--cols': device.cols }"
          >
            <!-- the signal / status strip every screen shares -->
            <div class="pm-lcd-bar pm-lcd-s" aria-hidden="true">
              <span class="pm-lcd-bar__sig" :class="{ 'is-off': !online || messageScreen?.kind === 'noservice' }">
                <i /><i /><i /><i />
              </span>
              <span v-if="!online && messageScreen?.kind !== 'noservice'" class="pm-lcd-bar__badge">NO SERVICE</span>
              <span v-else-if="isTwoway" class="pm-lcd-bar__title">{{ twowayTitle }}</span>
              <span v-else-if="notice" class="pm-lcd-bar__mem pm-lcd-bar__notice">{{ notice }}</span>
              <span v-else-if="screen === 'page' && current" class="pm-lcd-bar__mem">{{ memoryLine }}</span>
              <span v-if="lcdMore && !isTwoway && !thumb" class="pm-lcd-bar__more">▼</span>
              <span class="pm-lcd-bar__alert">{{ alertGlyph }}</span>
            </div>

            <!-- ── Message screens, both families ─────────────────────── -->
            <div
              v-if="messageScreen"
              class="pm-lcd-msg"
              :class="`pm-lcd-msg--${messageScreen.kind}`"
            >
              <div v-if="messageScreen.small" class="pm-lcd-s">{{ messageScreen.small }}</div>
              <div v-if="messageScreen.seg" class="pm-lcd-seg pm-lcd-l">
                <span class="sr-only">{{ messageScreen.big }}</span>
                <span class="pm-lcd-seg__ghost" aria-hidden="true">{{ ghostFor(messageScreen.seg) }}</span>
                <span class="pm-lcd-seg__ink" aria-hidden="true">{{ messageScreen.seg }}</span>
              </div>
              <div v-else class="pm-lcd-l">
                {{ messageScreen.big }}<span v-if="messageScreen.kind === 'activating'" class="pm-lcd-cursor" aria-hidden="true">_</span>
              </div>
              <div v-if="messageScreen.after" class="pm-lcd-s">{{ messageScreen.after }}</div>
            </div>

            <!-- ── numeric / alpha: one page at a time ───────────────── -->
            <div
              v-else-if="!isTwoway"
              class="pm-lcd-screen"
              :tabindex="thumb ? undefined : 0"
              :role="thumb ? undefined : 'group'"
              :aria-label="thumb ? undefined : 'Pager screen. Use the up and down arrow keys to step through pages.'"
              @keydown="onScreenKey"
              @pointerdown="onSwipeStart"
              @pointerup="onSwipeEnd"
              @pointercancel="swipe = null"
            >
              <div v-if="screen === 'wake'" class="pm-lcd-msg pm-lcd-msg--wake">
                <div class="pm-lcd-l">{{ unreadInbox }} NEW</div>
              </div>

              <div v-else-if="current" class="pm-lcd-page" :class="{ 'is-detail': detail, 'has-hint': !detail && !bigText && !!hintOf(current) }">
                <span class="sr-only">{{ pageLabel(current) }}</span>

                <div class="pm-lcd-page__in">
                  <div class="pm-lcd-status pm-lcd-s" aria-hidden="true">
                    <span v-if="!current.read" class="pm-lcd-new">NEW</span>
                    <span class="pm-lcd-status__from">{{ current.dir === 'out' ? 'TO' : 'FROM' }} {{ fromOf(current) }}</span>
                    <span v-if="current.locked" class="pm-lcd-lock">LOCKED</span>
                    <span class="pm-lcd-status__time">{{ whenShort(current.sentAt) }}</span>
                  </div>

                  <template v-if="!detail">
                    <!-- The message window: whole rows only, as many as the
                       glass holds. A longer page scrolls inside it. -->
                    <div
                      ref="win"
                      class="pm-lcd-win"
                      :class="{ 'is-scrolls': lcdScrolls }"
                      :tabindex="lcdScrolls && !thumb ? 0 : undefined"
                      :role="lcdScrolls && !thumb ? 'region' : undefined"
                      :aria-label="lcdScrolls && !thumb ? 'Page text. Scroll or use the arrow keys to read the rest.' : undefined"
                    >
                      <div
                        v-if="segOf(current.body)"
                        aria-hidden="true"
                        :key="`seg-${current.id}`"
                        class="pm-lcd-seg pm-lcd-l"
                        :class="{ 'is-snap': snapId === current.id }"
                      >
                        <span class="pm-lcd-seg__ghost">{{ ghostFor(segOf(current.body)) }}</span>
                        <span class="pm-lcd-seg__ink">{{ segOf(current.body) }}</span>
                      </div>
                      <div
                        v-else
                        aria-hidden="true"
                        class="pm-lcd-l pm-lcd-body"
                        :class="{
                          'pm-lcd-body--seg': isNumeric && isSegMixed(current.body),
                          'pm-lcd-body--ticker': tickerOn(current),
                        }"
                      >
                        <span
                          v-if="tickerOn(current)"
                          :key="`tick-${current.id}`"
                          class="pm-lcd-ticker__run"
                          data-motion
                          :style="{ '--chars': tickerChars(current.body) }"
                        ><PageText :body="current.body" /></span>
                        <PageText
                          v-else
                          :key="`txt-${current.id}`"
                          :body="current.body"
                          :type-on="typeId === current.id ? 25 : 0"
                        />
                      </div>
                    </div>
                    <div v-if="hintOf(current) && !bigText" class="pm-lcd-s pm-lcd-hint" aria-hidden="true">{{ hintOf(current) }}</div>
                  </template>

                  <dl v-else ref="win" class="pm-lcd-detail pm-lcd-s" aria-hidden="true">
                    <div v-if="current.groupId"><dt>GROUP</dt><dd>{{ groupOf(current) }}</dd></div>
                    <div v-if="current.peer"><dt>{{ current.dir === 'out' ? 'TO' : 'FROM' }}</dt><dd>{{ nameOf(current.peer) }}</dd></div>
                    <div v-if="current.peer"><dt>NO.</dt><dd>{{ formatNumber(current.peer) }}</dd></div>
                    <div><dt>TIME</dt><dd>{{ formatStamp(current.sentAt) }}</dd></div>
                    <div v-if="current.status"><dt>STATUS</dt><dd>{{ statusText(current) }}</dd></div>
                    <div v-if="current.locked"><dt>MEMO</dt><dd>LOCKED</dd></div>
                    <div v-if="hintOf(current)"><dt>CODE</dt><dd>{{ hintOf(current) }}</dd></div>
                  </dl>
                </div>
              </div>
            </div>

            <!-- ── twoway: the conversation list ─────────────────────── -->
            <ul
              v-else
              ref="convoList"
              class="pm-lcd-convos"
              :class="{ 'is-thumb': thumb, 'is-more': listMore }"
              role="list"
              :aria-label="thumb ? undefined : 'Conversations'"
              @scroll.passive="measureList"
            >
              <li v-for="(c, i) in convoRows" :key="c.key" :class="{ 'is-sel': i === sel }">
                <component
                  :is="thumb ? 'div' : 'button'"
                  :ref="(el) => { rowEls[i] = el; }"
                  class="pm-lcd-row"
                  :type="thumb ? undefined : 'button'"
                  :aria-current="!thumb && i === sel ? 'true' : undefined"
                  :aria-label="thumb ? undefined : convoLabel(c)"
                  :tabindex="thumb ? undefined : (i === sel ? 0 : -1)"
                  @click="openConvo(c, i)"
                  @keydown="onRowKey"
                >
                  <span class="pm-lcd-row__top pm-lcd-s" aria-hidden="true">
                    <span class="pm-lcd-row__dot">{{ c.unread ? '●' : '' }}</span>
                    <span class="pm-lcd-row__name">{{ rowName(c) }}</span>
                    <span class="pm-lcd-row__time">{{ c.last ? whenShort(c.last.sentAt) : '' }}</span>
                  </span>
                  <span class="pm-lcd-row__last pm-lcd-s" aria-hidden="true">
                    <template v-if="c.last">
                      <span v-if="c.last.dir === 'out'" class="pm-lcd-row__me">&gt; </span>
                      <span v-else-if="c.group" class="pm-lcd-row__me">{{ nameOf(c.last.peer) }}: </span>
                      <PageText :body="c.last.body" />
                      <span v-if="c.last.dir === 'out'" class="pm-lcd-row__mark">{{ deliveryMark(c.last) }}</span>
                    </template>
                    <template v-else-if="c.group">{{ c.group.members.length }} PAGERS · NO PAGES YET</template>
                  </span>
                </component>
              </li>
            </ul>
          </Lcd>
        </div>

        <!-- ── The keys: a real button over each drawn key ─────────────── -->
        <template v-if="!large">
          <component
            :is="keyTag"
            v-for="k in drawnKeys"
            :key="k.id"
            v-bind="keyAttrs(k)"
            class="pm-dev-key"
            :class="[`pm-dev-key--${k.id}`, `is-silk-${k.tone}`, `is-label-${k.labelSide}`, { 'is-label-vertical': k.vertical }]"
            :style="k.style"
            @click="press(k)"
          >
            <!-- The face is the drawn key, exactly; the button around it is
                 at least --tap each way, centred on it. -->
            <span class="pm-dev-key__face" :style="k.faceStyle" aria-hidden="true">
              <span class="pm-dev-key__cap" :style="k.capStyle" />
              <span class="pm-dev-key__silk" :style="k.silkStyle">{{ k.silk }}</span>
            </span>
          </component>
        </template>
      </div>
    </div>

    <!-- ── LARGE CONTROLS: the keys, as a plain 2-column grid ─────────── -->
    <div v-if="large" class="pm-dev-big" role="group" aria-label="Pager controls">
      <button
        v-for="k in bigKeys"
        :key="k.id"
        type="button"
        class="pm-dev-big__btn"
        :aria-label="k.name"
        :aria-disabled="k.disabled ? 'true' : undefined"
        @click="press(k)"
      >
        <span aria-hidden="true">{{ k.big }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import Lcd, { segForm, isSegMixed, hintLine, useLcdScroll } from './Lcd.vue';
import PageText from './PageText.vue';
import {
  me, activation, inbox, conversations, settings, lastArrival, unreadCount,
  markRead, toggleLock, deletePage, friendName, pageFrom, groupLabel,
} from '../store.js';
import { hintFor } from '../pm/codebook.js';
import {
  formatNumber, formatStamp, STATUS_WORD, deliveryMark, deliveryWord,
} from '../pm/format.js';
import { graphemes } from '../pm/text.js';
import { playBeep } from '../pm/beep.js';
import { artFor } from '../pm/device-art.js';
import { whenShort, whenLong } from './when.js';
import { haptic } from '../core/util.js';
import { tap } from '../core/audio.js';
import { announce, prefersReducedMotion } from '../core/a11y.js';

const props = defineProps({
  device: { type: Object, required: true },
  largeControls: { type: Boolean, default: false },
  /** Static render for the Devices list. */
  thumb: { type: Boolean, default: false },
  /** False while another screen is on top: pages are not marked read unseen. */
  active: { type: Boolean, default: true },
  /** Just activated, nothing sent or received yet: the LCD shows YOUR NUMBER. */
  firstRun: { type: Boolean, default: false },
  /** "What should friends see?" is open (or just answered): hold YOUR NUMBER / NAME: SAM
      on the glass even after the welcome page lands — it waits, marked NEW. */
  holdNumber: { type: Boolean, default: false },
});

const router = useRouter();
const lcd = ref(null);
const rowEls = ref([]);
const win = ref(null);

const isTwoway = computed(() => props.device.family === 'twoway');
const isNumeric = computed(() => props.device.family === 'numeric');
const isTicker = computed(() => props.device.id === 'topaz');
const large = computed(() => props.largeControls && !props.thumb);
const reduced = ref(prefersReducedMotion());

// ── the render and where things sit on it (src/pm/device-art.js) ─────────
const art = computed(() => artFor(props.device.id));
const pct = (n) => `${n}%`;
const rectStyle = (r) => ({ left: pct(r.x), top: pct(r.y), width: pct(r.w), height: pct(r.h) });

// --crop: the share of the render's height Large controls keeps — the glass
// and a margin under it (never into a key row).
const devStyle = computed(() => {
  const { screen, keys } = art.value;
  const below = screen.y + screen.h;
  const nextKey = Math.min(100, ...Object.values(keys).map((k) => k.y).filter((y) => y > below));
  return {
    '--aspect': art.value.aspect,
    '--crop': Math.min(below + 5, nextKey - 0.5) / 100,
  };
});
// The LCD text-size setting scales the glass's type — but the glass is the
// render's, a fixed size, so never past the point where the screen still holds
// its status rows and one whole row of the page (two list rows on a two-way).
// In % of the glass height, with ~10% of padding.
const glassMaxScale = computed(() => {
  const { s, l } = art.value.lcd;
  const need = isTwoway.value
    ? s * 1.18 * 5
    : s * 1.18 * 3.4 + s * 0.4 + l * (isNumeric.value ? 1.25 : 1.12);
  return Math.max(1, Math.floor((90 / need) * 100) / 100);
});
// Asked for bigger than that on a one-page screen (BUZZ 12, TOPAZ, MEMO): the
// PAGE text still grows — it is what the setting is for — and the codebook
// hint row gives up its room (the meaning is one READ away, in the details).
// Without this, M / L / XL changed nothing on the short-glass pagers.
const textSize = computed(() => Number(settings.textSize) || 1);
const bigText = computed(() => !isTwoway.value && textSize.value > glassMaxScale.value + 0.01);
const glassMaxScaleL = computed(() => {
  if (!bigText.value) return glassMaxScale.value;
  const { s, l } = art.value.lcd;
  const small = s * glassMaxScale.value * (1.18 * 2.3 + 0.4);
  const k = Math.floor(((90 - small) / (l * (isNumeric.value ? 1.25 : 1.12))) * 100) / 100;
  return Math.max(glassMaxScale.value, k);
});
const screenStyle = computed(() => ({
  ...rectStyle(art.value.screen),
  '--glass-s': art.value.lcd.s,
  '--glass-l': art.value.lcd.l,
  '--glass-k': glassMaxScale.value,
  '--glass-kl': glassMaxScaleL.value,
}));
const ledStyle = computed(() => {
  const l = art.value.led;
  return l ? { left: pct(l.x), top: pct(l.y), width: pct(l.d) } : {};
});

/** Where a key's legend sits, inside its button (so tapping it presses). */
function silkPlace(r) {
  const at = r.label;
  if (at && typeof at === 'object') {
    return {
      side: 'at',
      style: { left: pct(((at.x - r.x) / r.w) * 100), top: pct(((at.y - r.y) / r.h) * 100) },
    };
  }
  return { side: at || 'below', style: {} };
}

/** The drawn cap again, as the pressed state: the same pixels, pushed in. */
function capStyle(r) {
  const bx = r.w >= 100 ? 0 : (r.x / (100 - r.w)) * 100;
  const by = r.h >= 100 ? 0 : (r.y / (100 - r.h)) * 100;
  return {
    borderRadius: r.r || '0.4em',
    backgroundImage: `url(${art.value.src})`,
    backgroundSize: `${(100 / r.w) * 100}% ${(100 / r.h) * 100}%`,
    backgroundPosition: `${bx}% ${by}%`,
  };
}

// ── connectivity ─────────────────────────────────────────────────────────
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
const setOnline = () => { online.value = navigator.onLine !== false; };

// ── helpers ──────────────────────────────────────────────────────────────
// The display rule (your name ▸ their handle ▸ number), in LCD caps.
const nameOf = (n) => String(friendName(n) || formatNumber(n)).toUpperCase();
// A page's FROM / TO: "SAM", or "CREW · SAM" for a group page. The status row
// is one fixed line that ellipsizes, so the glass never grows.
const fromOf = (p) => String(pageFrom(p) || formatNumber(p.peer)).toUpperCase();
const groupOf = (p) => String(groupLabel(p.groupId, p.groupName)).toUpperCase();
const rowName = (c) => (c.group ? `${c.group.name} (${c.group.members.length})` : nameOf(c.friend.number));
const segOf = (body) => (isNumeric.value ? segForm(body) : '');
const ghostFor = (s) => '8'.repeat([...String(s)].length);
const hintOf = (p) => hintLine(p.body, hintFor(p.body));
const statusText = (p) => String(STATUS_WORD[p.status] || p.status || '').toUpperCase();
const tickerChars = (body) => graphemes(body).length;
// TOPAZ scrolls a page longer than its 16 columns across the glass; under
// reduced motion (or a short page) it simply wraps.
const tickerOn = (p) => isTicker.value && !reduced.value && !props.thumb
  && tickerChars(p?.body) > (props.device.cols || 16);
const alertGlyph = computed(() => ({ beep: '♪', vibrate: '≋', silent: '×' }[settings.alertMode] || ''));

function pageLabel(p) {
  const who = pageFrom(p) || formatNumber(p.peer);
  const hint = hintFor(p.body);
  const parts = [
    `${p.dir === 'out' ? 'Page to' : 'Page from'} ${who}, ${whenLong(p.sentAt)}.`,
    `${p.body}${hint ? ` — ${hint}` : ''}.`,
  ];
  if (p.status && STATUS_WORD[p.status]) parts.push(`${deliveryWord(p)}.`);
  if (!p.read) parts.push('New.');
  if (p.locked) parts.push('Locked.');
  return parts.join(' ');
}

// ── the inbox (numeric / alpha) ──────────────────────────────────────────
const list = computed(() => inbox.value || []);
const unreadInbox = computed(() => {
  if (isTwoway.value) return unreadCount?.value ?? 0;
  return list.value.filter((p) => !p.read).length;
});

const curId = ref(null);
const lastIdx = ref(0);
const detail = ref(false);
const wake = ref(false);
const typeId = ref(null);
const snapId = ref(null);

const current = computed(() => {
  const l = list.value;
  if (!l.length) return null;
  const byId = curId.value != null ? l.find((p) => p.id === curId.value) : null;
  return byId || l[Math.min(lastIdx.value, l.length - 1)];
});
watch(current, (p) => {
  if (!p) return;
  const i = list.value.indexOf(p);
  if (i >= 0) lastIdx.value = i;
  curId.value = p.id;
});

const screen = computed(() => {
  if (wake.value && unreadInbox.value > 0) return 'wake';
  return current.value ? 'page' : 'empty';
});
const memoryLine = computed(() => {
  const i = list.value.indexOf(current.value);
  return `MEMORY ${i + 1}/${list.value.length}`;
});

// The message window scrolls a page too long for the screen (see useLcdScroll).
// TOPAZ's ticker runs sideways instead, so its window never overflows.
const scroll = useLcdScroll({
  el: win,
  key: () => `${current.value?.id ?? ''}|${detail.value}|${screen.value}|${props.device.id}`,
  auto: () => !props.thumb && props.active && screen.value === 'page',
  reduced: () => reduced.value,
});
const lcdMore = scroll.more;
const lcdScrolls = scroll.overflows;

// Activation and empty states. Shared by both families.
const hasAnyPage = computed(() => (isTwoway.value ? convoRows.value.length > 0 : list.value.length > 0));
const numberSeg = computed(() => formatNumber(me.value?.number || ''));
const messageScreen = computed(() => {
  const state = activation.value;
  const number = me.value?.number;
  if (!number) {
    if (state === 'offline' || state === 'error') return { kind: 'noservice', big: 'NO SERVICE', after: 'NO NUMBER YET' };
    return { kind: 'activating', big: 'ACTIVATING…' };
  }
  if (hasAnyPage.value && !(props.holdNumber && !props.thumb)) return null;
  if ((props.firstRun || props.holdNumber) && !props.thumb) {
    return {
      kind: 'number',
      small: 'YOUR NUMBER',
      big: numberSeg.value,
      seg: isNumeric.value ? numberSeg.value : '',
      after: me.value?.handle ? `NAME: ${me.value.handle}` : '',
    };
  }
  return { kind: 'empty', big: 'NO PAGES', after: `YOUR # ${numberSeg.value}` };
});

// ── twoway ───────────────────────────────────────────────────────────────
const convoRows = computed(() => (conversations.value || []).filter((c) => c && (c.friend || c.group)));
const sel = ref(0);
const twowayTitle = computed(() => {
  const n = unreadInbox.value;
  return n ? `PAGES · ${n} NEW` : 'PAGES';
});
watch(() => convoRows.value.length, (n) => { if (sel.value >= n) sel.value = Math.max(0, n - 1); });

// More rows below the glass: the last visible row fades out, so a row cut in
// half by the bezel reads as "scroll for more", not as a drawing bug.
const convoList = ref(null);
const listMore = ref(false);
function measureList() {
  const el = convoList.value;
  listMore.value = !!el && el.scrollTop + el.clientHeight < el.scrollHeight - 2;
}
watch(
  () => [convoRows.value.length, settings.textSize, props.device.id],
  () => nextTick(measureList),
  { immediate: true },
);

function convoLabel(c) {
  const who = c.group
    ? `Group ${c.group.name}, ${c.group.members.length} pagers`
    : friendName(c.friend.number) || formatNumber(c.friend.number);
  const bits = [who];
  if (c.unread) bits.push(`${c.unread} new`);
  if (c.last) {
    bits.push(whenLong(c.last.sentAt));
    const by = c.last.dir === 'out' ? 'You' : (c.group ? friendName(c.last.peer) : 'They');
    bits.push(`${by}: ${c.last.body}`);
    if (c.last.dir === 'out' && STATUS_WORD[c.last.status]) bits.push(deliveryWord(c.last));
  } else if (c.group) {
    bits.push('No pages yet');
  }
  return bits.join('. ');
}

function openConvo(c, i) {
  if (props.thumb || !c) return;
  if (typeof i === 'number') sel.value = i;
  feedback();
  if (c.group) return router.push(`/tabs/group/${c.group.id}`);
  // Someone we only know by number: open their thread straight into "Save as",
  // so the list shows a name next time instead of 555-…
  const f = c.friend;
  const unnamed = !f.system && !String(f.name ?? '').trim() && !String(f.handle ?? '').trim();
  router.push(unnamed ? { path: `/tabs/thread/${f.number}`, query: { name: '1' } } : `/tabs/thread/${f.number}`);
}

async function moveSel(delta) {
  const n = convoRows.value.length;
  if (!n) return;
  let next = sel.value + delta;
  let wrapped = false;
  if (next < 0) { next = n - 1; wrapped = true; }
  if (next >= n) { next = 0; wrapped = true; }
  sel.value = next;
  if (wrapped) sound('double');
  await nextTick();
  const el = rowEls.value[next];
  el?.scrollIntoView?.({ block: 'nearest' });
  // Keep the keyboard where the selection is only when it already was in the list.
  if (document.activeElement?.classList?.contains('pm-lcd-row')) el?.focus?.();
  else announce(convoLabel(convoRows.value[next]));
}

function onRowKey(e) {
  if (e.key === 'ArrowDown') { e.preventDefault(); moveSel(1); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); moveSel(-1); }
}

// ── keys ─────────────────────────────────────────────────────────────────
const selName = computed(() => {
  const c = convoRows.value[sel.value];
  if (!c) return '';
  return c.group ? c.group.name : (friendName(c.friend.number) || formatNumber(c.friend.number));
});

const KEYS = {
  up: { id: 'up', silk: '▲', big: '▲ Newer' },
  down: { id: 'down', silk: '▼', big: '▼ Older' },
  read: { id: 'read', silk: 'READ', big: 'Read' },
  reply: { id: 'reply', silk: 'REPLY', big: 'Reply' },
  new: { id: 'new', silk: 'NEW', big: 'New page' },
  lock: { id: 'lock', silk: 'LOCK', big: 'Lock' },
  del: { id: 'del', silk: 'DEL', big: 'Delete' },
  open: { id: 'open', silk: 'OPEN', big: 'Open' },
  compose: { id: 'compose', silk: 'COMPOSE', big: 'Compose' },
  mode: { id: 'mode', silk: 'MODE', big: 'Devices' },
  setup: { id: 'setup', silk: 'SETUP', big: 'Settings' },
};

function nameFor(id) {
  const p = current.value;
  switch (id) {
    case 'up': return isTwoway.value ? 'Previous conversation' : 'Newer page';
    case 'down': return isTwoway.value ? 'Next conversation' : 'Older page';
    case 'read':
      if (scroll.more.value) return 'Read on (next line)';
      return detail.value ? 'Read page (hide details)' : 'Read details';
    case 'reply': return p ? `Reply to ${p.groupId ? groupOf(p) : (friendName(p.peer) || formatNumber(p.peer))}` : 'Reply';
    case 'new': return 'New page';
    case 'lock': return p?.locked ? 'Unlock this page' : 'Lock this page';
    case 'del': return p?.locked ? 'Delete this page (locked)' : 'Delete this page';
    case 'open': return selName.value ? `Open ${selName.value}` : 'Open';
    case 'compose': return 'Compose a page';
    // The destination first, so "Devices" / "Settings" find them; the printed
    // legend is in the name too (label-in-name, for voice control).
    case 'mode': return props.largeControls ? 'Devices' : 'Devices (MODE)';
    case 'setup': return props.largeControls ? 'Settings' : 'Settings (SETUP)';
    default: return id;
  }
}
function isDisabled(id) {
  if (id === 'mode' || id === 'setup') return false;
  if (isTwoway.value) return ['up', 'down', 'open'].includes(id) && !convoRows.value.length;
  if (['up', 'down'].includes(id)) return list.value.length < 1;
  if (['read', 'reply', 'lock', 'del'].includes(id)) return !current.value;
  return false;
}
const withMeta = (id) => ({ ...KEYS[id], name: nameFor(id), disabled: isDisabled(id) });

// Every key the render has, in the art's order: a real button over each.
const drawnKeys = computed(() => Object.entries(art.value.keys)
  .filter(([id]) => KEYS[id])
  .map(([id, r]) => {
    const place = silkPlace(r);
    return {
      ...withMeta(id),
      tone: r.tone || art.value.silk,
      vertical: !!r.vertical,
      labelSide: place.side,
      silkStyle: place.style,
      // Centred on the drawn key; its size (≥ --tap) is in device.css.
      style: { left: pct(r.x + r.w / 2), top: pct(r.y + r.h / 2), '--kw': r.w, '--kh': r.h },
      faceStyle: { borderRadius: r.r || '0.4em' },
      capStyle: capStyle(r),
    };
  }));
const bigKeys = computed(() => {
  const ids = isTwoway.value
    ? ['up', 'down', 'open', 'compose', 'mode', 'setup']
    : ['up', 'down', 'read', 'reply', 'lock', 'del', 'mode', 'setup', 'new'];
  return ids.map((id) => {
    const k = withMeta(id);
    // The visible label is always contained in the accessible name.
    if (id === 'lock' && current.value?.locked) k.big = 'Unlock';
    if (id === 'up') k.big = isTwoway.value ? '▲ Previous' : '▲ Newer';
    if (id === 'down') k.big = isTwoway.value ? '▼ Next' : '▼ Older';
    return k;
  });
});

const keyTag = computed(() => (props.thumb ? 'span' : 'button'));
function keyAttrs(k) {
  if (props.thumb) return {};
  return {
    type: 'button',
    'aria-label': k.name ?? nameFor(k.id),
    'aria-disabled': (k.disabled ?? isDisabled(k.id)) ? 'true' : undefined,
  };
}

function sound(kind) {
  if (settings.alertMode === 'silent') return;
  if (kind === 'double') playBeep('double');
  else tap();
}
function feedback() {
  haptic('tick');
  sound('tap');
  lcd.value?.light?.();
}

function press(k) {
  if (props.thumb) return;
  feedback();
  wake.value = false;
  notice.value = '';
  if (k.disabled ?? isDisabled(k.id)) return;
  switch (k.id) {
    case 'up': return isTwoway.value ? moveSel(-1) : step(-1);
    case 'down': return isTwoway.value ? moveSel(1) : step(1);
    case 'read':
      // More text below: READ scrolls on a row, as it did on the real thing.
      // At the end of the page it shows (or hides) the details.
      if (scroll.more.value && scroll.step()) return undefined;
      detail.value = !detail.value;
      if (current.value) announce(detail.value ? detailLabel(current.value) : pageLabel(current.value));
      return undefined;
    case 'reply': {
      const p = current.value;
      if (!p) return undefined;
      // A group page is answered to the group.
      return router.push(p.groupId ? `/tabs/compose/group-${p.groupId}` : `/tabs/compose/${p.peer}`);
    }
    case 'new': return router.push('/tabs/compose');
    case 'compose': return router.push('/tabs/compose');
    case 'mode': return router.push('/tabs/devices');
    case 'setup': return router.push('/tabs/settings');
    case 'open': return openConvo(convoRows.value[sel.value], sel.value);
    case 'lock': {
      const p = current.value;
      if (!p) return undefined;
      toggleLock(p.id); // the store announces "Page locked."
      return undefined;
    }
    case 'del': {
      const p = current.value;
      if (!p) return undefined;
      // A locked page is protected, as it was on the real thing: DEL refuses
      // with the pager's double beep and says why, rather than deleting it.
      if (p.locked) {
        sound('double');
        haptic('warning');
        flash('LOCKED · UNLOCK FIRST');
        announce('This page is locked. Press LOCK to unlock it, then delete it.');
        return undefined;
      }
      detail.value = false;
      // Heavier than a key tick: something was removed (undo is in the toast).
      haptic('pop');
      deletePage(p.id);
      return undefined;
    }
    default: return undefined;
  }
}

// A one-line message in the LCD's status strip, for a second and a half.
const notice = ref('');
let noticeTimer = null;
function flash(text) {
  notice.value = text;
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => { notice.value = ''; noticeTimer = null; }, 1600);
}

function detailLabel(p) {
  const who = pageFrom(p) || formatNumber(p.peer);
  const hint = hintFor(p.body);
  return [
    p.peer ? `From ${who}, ${formatNumber(p.peer)}.` : `To ${who}.`,
    `${formatStamp(p.sentAt)}.`,
    STATUS_WORD[p.status] ? `${STATUS_WORD[p.status]}.` : '',
    p.locked ? 'Locked.' : '',
    hint ? `Code: ${hint}.` : '',
  ].filter(Boolean).join(' ');
}

// ▲ = newer (towards index 0), ▼ = older. Wraps at either end with a double beep.
function step(delta) {
  const l = list.value;
  if (!l.length) return;
  const i = Math.max(0, l.indexOf(current.value));
  let next = i + delta;
  if (next < 0 || next >= l.length) {
    next = (next + l.length) % l.length;
    sound('double');
  }
  detail.value = false;
  curId.value = l[next].id;
  lastIdx.value = next;
  announce(`${next + 1} of ${l.length}. ${pageLabel(l[next])}`);
}

function onScreenKey(e) {
  if (props.thumb) return;
  if (e.key === 'ArrowDown') { e.preventDefault(); press(withMeta('down')); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); press(withMeta('up')); }
  else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); press(withMeta('read')); }
}

// Swipe up / down on the glass steps, like the keys.
const swipe = ref(null);
const SWIPE_MIN = 28;
const TAP_MAX = 8;
function onSwipeStart(e) {
  if (props.thumb) return;
  swipe.value = { y: e.clientY, x: e.clientX };
}
function onSwipeEnd(e) {
  const s = swipe.value;
  swipe.value = null;
  if (!s) return;
  const dy = e.clientY - s.y;
  const dx = e.clientX - s.x;
  // A tap on the glass (no real movement) scrolls a long page on a row.
  if (Math.abs(dy) < TAP_MAX && Math.abs(dx) < TAP_MAX) {
    if (screen.value === 'page' && scroll.step()) feedback();
    // Nothing to scroll: the tap still answers — the backlight comes on, as
    // pressing anything on a real pager did (no beep: nothing happened).
    else lcd.value?.light?.();
    return;
  }
  // A page longer than the glass scrolls under your finger instead; stepping
  // pages is then the ▲ / ▼ keys' job.
  if (scroll.overflows.value) return;
  if (Math.abs(dy) < SWIPE_MIN || Math.abs(dy) < Math.abs(dx)) return;
  press(withMeta(dy < 0 ? 'down' : 'up'));
}

// ── read marking: displayed for a second → read ──────────────────────────
let readTimer = null;
watch(
  () => [current.value?.id, current.value?.read, screen.value, props.active, props.thumb, !!messageScreen.value],
  () => {
    if (readTimer) clearTimeout(readTimer);
    readTimer = null;
    const p = current.value;
    // Not while a message screen (YOUR NUMBER held for naming) covers the page.
    if (messageScreen.value) return;
    if (props.thumb || !props.active || isTwoway.value || !p || p.read || screen.value !== 'page') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    readTimer = setTimeout(() => { readTimer = null; markRead(p.id); }, 1000);
  },
  { immediate: true },
);

// ── arrival: jump to it, light up, shake, type it on ─────────────────────
const arrivalKey = computed(() => lastArrival.value?.id ?? null);
const shaking = ref(false);
let shakeTimer = null;
let snapTimer = null;
watch(arrivalKey, (id, before) => {
  if (props.thumb || id == null || id === before) return;
  const page = lastArrival.value;
  wake.value = false;
  detail.value = false;
  if (!isTwoway.value && list.value.some((p) => p.id === id)) {
    curId.value = id;
    if (isNumeric.value && segForm(page?.body)) {
      snapId.value = id;
      if (snapTimer) clearTimeout(snapTimer);
      snapTimer = setTimeout(() => { snapId.value = null; }, 600);
    } else {
      typeId.value = id;
    }
  }
  if (isTwoway.value) sel.value = 0;
  if (!prefersReducedMotion()) {
    shaking.value = false;
    requestAnimationFrame(() => { shaking.value = true; });
    if (shakeTimer) clearTimeout(shakeTimer);
    shakeTimer = setTimeout(() => { shaking.value = false; }, 400);
  }
});

// ── wake: "12 NEW" when the pager is looked at with unread pages ─────────
let wakeTimer = null;
function wakeUp() {
  if (props.thumb || isTwoway.value || unreadInbox.value < 1) return;
  wake.value = true;
  if (wakeTimer) clearTimeout(wakeTimer);
  wakeTimer = setTimeout(() => {
    wakeTimer = null;
    if (!wake.value) return;
    wake.value = false;
    const firstUnread = list.value.find((p) => !p.read);
    if (firstUnread) curId.value = firstUnread.id;
  }, 1500);
}
watch(() => props.active, (on, was) => { if (on && !was) wakeUp(); });
const onVisible = () => { if (document.visibilityState === 'visible' && props.active) wakeUp(); };

let motionQuery = null;
const onMotion = () => { reduced.value = prefersReducedMotion(); };

onMounted(() => {
  if (props.thumb) return;
  addEventListener('online', setOnline);
  addEventListener('offline', setOnline);
  document.addEventListener('visibilitychange', onVisible);
  try {
    motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    motionQuery.addEventListener?.('change', onMotion);
  } catch { motionQuery = null; }
  wakeUp();
  document.fonts?.ready?.then(measureList);
});

onBeforeUnmount(() => {
  removeEventListener('online', setOnline);
  removeEventListener('offline', setOnline);
  document.removeEventListener('visibilitychange', onVisible);
  motionQuery?.removeEventListener?.('change', onMotion);
  for (const t of [readTimer, shakeTimer, snapTimer, wakeTimer, noticeTimer]) if (t) clearTimeout(t);
});

defineExpose({ light: () => lcd.value?.light?.() });
</script>
